import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, Lock, Database, Search, ArrowLeft, ExternalLink, Code, BookOpen, Key, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ApiKeyManager from '@/components/api/ApiKeyManager';

const API_BASE_URL = 'https://bpxebrbhhywoayvqwdnh.supabase.co/functions/v1/inventory-api';
const SYNC_API_URL = 'https://bpxebrbhhywoayvqwdnh.supabase.co/functions/v1/sync-inventory';
export default function ApiDocumentation() {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Gekopieerd naar klembord');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, id, language = 'bash' }: { code: string; id: string; language?: string }) => (
    <div className="relative group">
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code className={`language-${language}`}>{code}</code>
      </pre>
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => copyToClipboard(code, id)}
      >
        {copiedCode === id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );

  // Basic Auth examples
  const curlBasicAuth = `curl -X GET "${API_BASE_URL}?q=primer&limit=10" \\
  -H "Authorization: Basic $(echo -n 'username:password' | base64)"`;

  // API Key examples
  const curlApiKey = `curl -X GET "${API_BASE_URL}?q=primer&limit=10" \\
  -H "X-API-Key: inv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -H "X-API-Secret: your_secret_here"`;

  const curlExampleWarehouse = `curl -X GET "${API_BASE_URL}?warehouse=2&limit=50" \\
  -H "X-API-Key: your_api_key" \\
  -H "X-API-Secret: your_api_secret"`;

  const pythonExample = `import requests

# API credentials (API Key methode)
api_key = "inv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
api_secret = "your_secret_here"

# Make request
response = requests.get(
    "${API_BASE_URL}",
    headers={
        "X-API-Key": api_key,
        "X-API-Secret": api_secret
    },
    params={
        "q": "primer",
        "warehouse": "2",
        "limit": 100,
        "offset": 0
    }
)

data = response.json()
print(f"Gevonden: {data['pagination']['total']} producten")

for item in data['data']:
    print(f"{item['product_naam']}: {item['voorraad']} {item['eenheid']}")`;

  const javascriptExample = `// JavaScript / Node.js voorbeeld
const apiKey = 'inv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
const apiSecret = 'your_secret_here';

const response = await fetch(
  '${API_BASE_URL}?q=primer&limit=10',
  {
    headers: {
      'X-API-Key': apiKey,
      'X-API-Secret': apiSecret
    }
  }
);

const data = await response.json();

console.log(\`Gevonden: \${data.pagination.total} producten\`);
data.data.forEach(item => {
  console.log(\`\${item.product_naam}: \${item.voorraad} \${item.eenheid}\`);
});`;

  const phpExample = `<?php
$apiKey = 'inv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
$apiSecret = 'your_secret_here';

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, '${API_BASE_URL}?q=primer&limit=10');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "X-API-Key: $apiKey",
    "X-API-Secret: $apiSecret"
]);

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);

echo "Gevonden: " . $data['pagination']['total'] . " producten\\n";

foreach ($data['data'] as $item) {
    echo $item['product_naam'] . ": " . $item['voorraad'] . " " . $item['eenheid'] . "\\n";
}
?>`;

  const responseExample = `{
  "success": true,
  "data": [
    {
      "product_id": "12345",
      "bc": "8712345678901",
      "product_naam": "Glidden Aqua Primer 2.5L",
      "eenheid": "ST",
      "magazijn": "1",
      "locatie": "A-12-3",
      "voorraad": 45,
      "moq": 1,
      "itemassortment": "VERF",
      "vrije_waarde_2": 2.5,
      "timestamp_sync": "2025-01-16T10:30:00Z"
    }
  ],
  "pagination": {
    "total": 156,
    "limit": 10,
    "offset": 0,
    "hasMore": true
  },
  "filters": {
    "query": "primer",
    "warehouse": null
  },
  "timestamp": "2025-01-16T10:35:22Z"
}`;

  // Sync inventory examples
  const syncRequestExample = `[
  {
    "product_id": "ABC123",
    "bc": "8710123456789",
    "product_naam": "Product Naam",
    "eenheid": "stuk",
    "magazijn": "1",
    "locatie": "A-01-02",
    "voorraad": 150,
    "moq": 10,
    "itemassortment": "A",
    "vrije_waarde_2": "2,5"
  }
]`;

  const syncResponseExample = `{
  "success": true,
  "message": "Successfully synced 1500 inventory items",
  "stats": {
    "received": 1500,
    "deduplicated": 1450,
    "upserted": 1450,
    "errors": 0
  },
  "duration_ms": 2340
}`;

  const syncPartialResponseExample = `{
  "success": false,
  "message": "Synced with 5 errors",
  "stats": {
    "received": 100,
    "deduplicated": 100,
    "upserted": 95,
    "errors": 5
  },
  "duration_ms": 1200
}`;

  const curlSyncExample = `curl -X POST "${SYNC_API_URL}" \\
  -u "username:password" \\
  -H "Content-Type: application/json" \\
  -d '[{
    "product_id": "ABC123",
    "magazijn": "1",
    "bc": "8710123456789",
    "product_naam": "Product Naam",
    "eenheid": "stuk",
    "locatie": "A-01-02",
    "voorraad": 150,
    "moq": 10,
    "itemassortment": "A",
    "vrije_waarde_2": "2,5"
  }]'`;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Database className="h-8 w-8 text-primary" />
              Inventory API Documentatie
            </h1>
            <p className="text-muted-foreground mt-1">
              REST API voor het ophalen en synchroniseren van voorraadgegevens
            </p>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Authenticatie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="secondary">API Key</Badge>
                <Badge variant="outline">Basic Auth</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                API Key of Basic Auth vereist
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Code className="h-4 w-4" />
                Formaat
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant="secondary">JSON</Badge>
              <p className="text-sm text-muted-foreground mt-2">
                Alle responses zijn in JSON formaat
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-4 w-4" />
                Rate Limit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Badge variant="secondary">1000/uur</Badge>
              <p className="text-sm text-muted-foreground mt-2">
                Max 1000 requests per uur
              </p>
            </CardContent>
          </Card>
        </div>

        {/* API Key Manager */}
        <div className="mb-8">
          <ApiKeyManager />
        </div>

        {/* Base URL */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Base URL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CodeBlock code={API_BASE_URL} id="base-url" />
          </CardContent>
        </Card>

        {/* Authentication Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Authenticatie
            </CardTitle>
            <CardDescription>
              De API ondersteunt twee authenticatie methodes: API Keys (aanbevolen) en Basic Auth
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Let op:</strong> Bewaar je credentials veilig en deel ze nooit in publieke code of repositories.
              </p>
            </div>
            
            <Tabs defaultValue="apikey">
              <TabsList>
                <TabsTrigger value="apikey" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  API Key (Aanbevolen)
                </TabsTrigger>
                <TabsTrigger value="basic" className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Basic Auth
                </TabsTrigger>
              </TabsList>

              <TabsContent value="apikey" className="space-y-4 mt-4">
                <div>
                  <h4 className="font-medium mb-2">Headers</h4>
                  <CodeBlock 
                    code={`X-API-Key: inv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
X-API-Secret: your_secret_here`} 
                    id="apikey-header" 
                  />
                </div>
                <div>
                  <h4 className="font-medium mb-2">Voordelen API Key</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                    <li>Eenvoudig te beheren via deze pagina</li>
                    <li>Kan per applicatie worden aangemaakt</li>
                    <li>Direct te deactiveren of verwijderen</li>
                    <li>Gebruik wordt bijgehouden</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Voorbeeld (curl)</h4>
                  <CodeBlock code={curlApiKey} id="apikey-example" />
                </div>
              </TabsContent>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div>
                  <h4 className="font-medium mb-2">Authorization Header</h4>
                  <CodeBlock 
                    code={`Authorization: Basic base64(username:password)`} 
                    id="basic-header" 
                  />
                </div>
                <div>
                  <h4 className="font-medium mb-2">Voorbeeld (curl)</h4>
                  <CodeBlock code={curlBasicAuth} id="basic-example" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Basic Auth credentials worden verstrekt door de systeembeheerder.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Endpoints */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Endpoints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* GET /inventory-api */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-green-600">GET</Badge>
                  <code className="text-sm font-mono">/inventory-api</code>
                </div>
                
                <p className="text-muted-foreground mb-4">
                  Haalt voorraadgegevens op met optionele zoek- en filterparameters.
                </p>

                <h4 className="font-medium mb-2">Query Parameters</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4">Parameter</th>
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-left py-2 pr-4">Verplicht</th>
                        <th className="text-left py-2">Beschrijving</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>q</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Zoekterm (min. 2 karakters). Zoekt in productnaam, barcode (bc) en SKU.</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>warehouse</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Filter op magazijn nummer. Beschikbare magazijnen: 2, 5, 7, 8, 12</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>limit</code></td>
                        <td className="py-2 pr-4">integer</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Maximum aantal resultaten (standaard: 100, max: 1000)</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4"><code>offset</code></td>
                        <td className="py-2 pr-4">integer</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Offset voor paginatie (standaard: 0)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* POST /sync-inventory */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-blue-600">POST</Badge>
                  <code className="text-sm font-mono">/sync-inventory</code>
                  <Badge variant="outline" className="ml-2">
                    <Upload className="h-3 w-3 mr-1" />
                    Upload
                  </Badge>
                </div>
                
                <p className="text-muted-foreground mb-4">
                  Synchroniseert voorraadgegevens naar de database (upsert op <code>product_id</code> + <code>magazijn</code>).
                </p>

                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Authenticatie:</strong> Basic Auth (verplicht). Content-Type: application/json
                  </p>
                </div>

                <h4 className="font-medium mb-2">Request Body Velden</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4">Veld</th>
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-left py-2 pr-4">Verplicht</th>
                        <th className="text-left py-2">Beschrijving</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>product_id</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Ja</td>
                        <td className="py-2">Unieke product identifier</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>magazijn</code></td>
                        <td className="py-2 pr-4">string/number</td>
                        <td className="py-2 pr-4">Ja</td>
                        <td className="py-2">Magazijn nummer (samen met product_id unieke sleutel)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>bc</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Barcode / EAN code</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>product_naam</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Productnaam</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>eenheid</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Eenheid (stuk, kg, etc.)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>locatie</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Magazijnlocatie</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>voorraad</code></td>
                        <td className="py-2 pr-4">number</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Voorraadaantal</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>moq</code></td>
                        <td className="py-2 pr-4">number</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Minimum order quantity</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 pr-4"><code>itemassortment</code></td>
                        <td className="py-2 pr-4">string</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Assortiment categorie</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4"><code>vrije_waarde_2</code></td>
                        <td className="py-2 pr-4">string/number</td>
                        <td className="py-2 pr-4">Nee</td>
                        <td className="py-2">Fysiek gewicht (accepteert "2,5" of "2.5")</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <h5 className="font-medium text-sm mb-2">Alternatieve body formaten</h5>
                  <p className="text-sm text-muted-foreground">
                    Naast een directe array <code>[...]</code> worden ook ondersteund:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-1">
                    <li><code>{`{ "body": [...] }`}</code></li>
                    <li><code>{`{ "items": [...] }`}</code></li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Format */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Response Formaat</CardTitle>
            <CardDescription>
              Alle responses bevatten de volgende structuur
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-medium">Velden per product</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Veld</th>
                    <th className="text-left py-2 pr-4">Type</th>
                    <th className="text-left py-2">Beschrijving</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>product_id</code></td>
                    <td className="py-2 pr-4">string</td>
                    <td className="py-2">Unieke product identificatie</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>bc</code></td>
                    <td className="py-2 pr-4">string | null</td>
                    <td className="py-2">Barcode / EAN code</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>product_naam</code></td>
                    <td className="py-2 pr-4">string</td>
                    <td className="py-2">Productnaam</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>eenheid</code></td>
                    <td className="py-2 pr-4">string | null</td>
                    <td className="py-2">Eenheid (bijv. "ST", "L", "KG")</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>magazijn</code></td>
                    <td className="py-2 pr-4">string | null</td>
                    <td className="py-2">Magazijn nummer</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>locatie</code></td>
                    <td className="py-2 pr-4">string | null</td>
                    <td className="py-2">Locatie in magazijn</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>voorraad</code></td>
                    <td className="py-2 pr-4">integer</td>
                    <td className="py-2">Huidige voorraad</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>moq</code></td>
                    <td className="py-2 pr-4">integer | null</td>
                    <td className="py-2">Minimum order quantity</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>itemassortment</code></td>
                    <td className="py-2 pr-4">string | null</td>
                    <td className="py-2">Productcategorie</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><code>vrije_waarde_2</code></td>
                    <td className="py-2 pr-4">number | null</td>
                    <td className="py-2">Fysiek gewicht (Vrije waarde 2)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4"><code>timestamp_sync</code></td>
                    <td className="py-2 pr-4">string</td>
                    <td className="py-2">Laatste synchronisatie (ISO 8601)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4 className="font-medium mt-6">Voorbeeld Response (GET)</h4>
            <CodeBlock code={responseExample} id="response-example" language="json" />
          </CardContent>
        </Card>

        {/* Sync Response Format */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Sync Response Formaat
            </CardTitle>
            <CardDescription>
              Response structuur voor POST /sync-inventory
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Success Response (200 OK)</h4>
              <CodeBlock code={syncResponseExample} id="sync-response-success" language="json" />
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Partial Success (207 Multi-Status)</h4>
              <CodeBlock code={syncPartialResponseExample} id="sync-response-partial" language="json" />
            </div>

            <div className="mt-4">
              <h4 className="font-medium mb-2">Stats Object</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Veld</th>
                      <th className="text-left py-2 pr-4">Type</th>
                      <th className="text-left py-2">Beschrijving</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code>received</code></td>
                      <td className="py-2 pr-4">integer</td>
                      <td className="py-2">Aantal ontvangen items in de request</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code>deduplicated</code></td>
                      <td className="py-2 pr-4">integer</td>
                      <td className="py-2">Aantal items na deduplicatie (op product_id + magazijn)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 pr-4"><code>upserted</code></td>
                      <td className="py-2 pr-4">integer</td>
                      <td className="py-2">Aantal succesvol opgeslagen items</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4"><code>errors</code></td>
                      <td className="py-2 pr-4">integer</td>
                      <td className="py-2">Aantal items met fouten</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Code Examples */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Code Voorbeelden</CardTitle>
            <CardDescription>
              Voorbeelden in verschillende programmeertalen (met API Key authenticatie)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="curl" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="curl">cURL</TabsTrigger>
                <TabsTrigger value="python">Python</TabsTrigger>
                <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                <TabsTrigger value="php">PHP</TabsTrigger>
              </TabsList>

              <TabsContent value="curl" className="space-y-4 mt-4">
                <div>
                  <h4 className="font-medium mb-2">GET: Zoeken op productnaam</h4>
                  <CodeBlock code={curlApiKey} id="curl-search" />
                </div>
                <div>
                  <h4 className="font-medium mb-2">GET: Filteren op magazijn</h4>
                  <CodeBlock code={curlExampleWarehouse} id="curl-warehouse" />
                </div>
                <div>
                  <h4 className="font-medium mb-2">POST: Voorraad synchroniseren</h4>
                  <CodeBlock code={curlSyncExample} id="curl-sync" />
                </div>
              </TabsContent>

              <TabsContent value="python" className="mt-4">
                <CodeBlock code={pythonExample} id="python-example" language="python" />
              </TabsContent>

              <TabsContent value="javascript" className="mt-4">
                <CodeBlock code={javascriptExample} id="js-example" language="javascript" />
              </TabsContent>

              <TabsContent value="php" className="mt-4">
                <CodeBlock code={phpExample} id="php-example" language="php" />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Error Codes */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>HTTP Status Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Code</th>
                    <th className="text-left py-2 pr-4">Status</th>
                    <th className="text-left py-2">Beschrijving</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="default" className="bg-green-600">200</Badge></td>
                    <td className="py-2 pr-4">OK</td>
                    <td className="py-2">Request succesvol verwerkt</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="default" className="bg-amber-600">207</Badge></td>
                    <td className="py-2 pr-4">Multi-Status</td>
                    <td className="py-2">Sync gedeeltelijk succesvol (met fouten)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="destructive">400</Badge></td>
                    <td className="py-2 pr-4">Bad Request</td>
                    <td className="py-2">Ongeldige payload format</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="destructive">401</Badge></td>
                    <td className="py-2 pr-4">Unauthorized</td>
                    <td className="py-2">Ongeldige of ontbrekende credentials (API Key of Basic Auth)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="destructive">405</Badge></td>
                    <td className="py-2 pr-4">Method Not Allowed</td>
                    <td className="py-2">Method niet toegestaan voor dit endpoint</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 pr-4"><Badge variant="destructive">429</Badge></td>
                    <td className="py-2 pr-4">Too Many Requests</td>
                    <td className="py-2">Rate limit overschreden</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4"><Badge variant="destructive">500</Badge></td>
                    <td className="py-2 pr-4">Internal Server Error</td>
                    <td className="py-2">Server fout, neem contact op met beheerder</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Support */}
        <Card>
          <CardHeader>
            <CardTitle>Ondersteuning</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Voor vragen over de API, maak een nieuwe API Key aan via deze pagina of neem contact op met de systeembeheerder.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
